import { ChevronDown, PlusCircle, Search } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ClientInterface } from '@/interfaces/ServerInterfaces'
import { cn } from '@/lib/utils'

import { useClientSearch } from '../hooks/useClientSearch'
import { ClientData } from '../types'

interface ClientSelectorProps {
  selectedClient?: ClientData
  onClientSelect: (_client: ClientData | null) => void
  onNewClientClick?: (_searchQuery: string) => void
  onFocus?: () => void
  required?: boolean
  disabled?: boolean
  error?: string
  className?: string
  justSelector?: boolean
  showLabel?: boolean
  triggerIcon?: React.ReactNode
  triggerLabel?: string | null
}

// Client skeleton component for loading state
const ClientSkeleton = () => (
  <div className="p-2">
    <div className="flex items-start justify-between">
      <div className="flex-1 space-y-1">
        <div className="h-4 w-1/4 animate-pulse rounded bg-muted" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
        <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
      </div>
    </div>
  </div>
)

export function ClientSelector({
  selectedClient,
  onClientSelect,
  onNewClientClick,
  onFocus,
  required = false,
  disabled = false,
  error,
  className,
  justSelector = false,
  showLabel = true,
  triggerIcon,
  triggerLabel,
}: ClientSelectorProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const {
    searchQuery,
    setSearchQuery,
    getClientsWithSelected,
    isLoadingBusinessClients,
    isLoadingMore,
    handleClientsScroll,
  } = useClientSearch()

  const clientsToShow = getClientsWithSelected(selectedClient)

  const handleClientSelect = (client: ClientData | null) => {
    onClientSelect(client)
    setSearchQuery('')
    setIsPopoverOpen(false)
  }

  const handleNewClientClick = () => {
    onNewClientClick?.(searchQuery)
    setSearchQuery('')
    setIsPopoverOpen(false)
  }

  return (
    <div className={className}>
      <div
        className={
          triggerLabel ? 'flex items-end gap-2' : 'mb-2 flex items-end gap-2'
        }
      >
        <div className="flex-1">
          {showLabel && (
            <Label htmlFor="client-select">Cliente{required && ' (*)'}</Label>
          )}
          <Popover
            modal={true}
            onOpenChange={setIsPopoverOpen}
            open={isPopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button
                aria-expanded={isPopoverOpen}
                className={cn(
                  triggerLabel
                    ? 'w-full justify-between py-2 font-normal'
                    : 'mt-2 h-16 w-full justify-between font-normal',
                  error ? 'border-red-500' : ''
                )}
                disabled={disabled}
                role="combobox"
                variant="outline"
              >
                {triggerIcon && (
                  <div className="mr-2 flex items-center">{triggerIcon}</div>
                )}

                {selectedClient ? (
                  <div className="flex flex-col items-start">
                    <span className="font-medium">
                      {selectedClient.name ||
                        `${selectedClient.firstName || ''} ${selectedClient.lastName || ''}`.trim()}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {selectedClient.email || 'Sin email'}
                    </span>
                  </div>
                ) : (
                  <span
                    className={
                      triggerLabel ? 'font-medium' : 'text-muted-foreground'
                    }
                  >
                    {triggerLabel ?? 'Seleccionar Cliente'}
                  </span>
                )}

                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[--radix-popover-trigger-width] p-0"
              onWheelCapture={(e) => e.stopPropagation()}
            >
              <div className="px-3 pt-3">
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    autoFocus
                    className="pl-9"
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={onFocus}
                    placeholder="Buscar por nombre, correo o CI..."
                    value={searchQuery}
                  />
                </div>
              </div>
              <div
                className="max-h-[250px] overflow-y-auto"
                onScroll={handleClientsScroll} // Enable infinite scroll
                onWheel={(e) => {
                  e.stopPropagation()
                }}
                style={{
                  scrollBehavior: 'auto',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {isLoadingBusinessClients ? (
                  <>
                    <ClientSkeleton />
                    <ClientSkeleton />
                    <ClientSkeleton />
                  </>
                ) : clientsToShow.length > 0 ? (
                  <>
                    {clientsToShow.map(
                      (client: ClientData | ClientInterface) => {
                        const isSelected =
                          selectedClient && selectedClient.id === client.id

                        return (
                          <div
                            className={cn(
                              'cursor-pointer p-2 hover:bg-muted',
                              isSelected &&
                                'border-l-2 border-l-primary bg-muted/50'
                            )}
                            key={client.id}
                            onClick={() => {
                              // Convert to ClientData format if needed
                              const clientData: ClientData = {
                                id: client.id,
                                name:
                                  client.name ||
                                  `${client.firstName || ''} ${client.lastName || ''}`.trim(),
                                firstName: client.firstName,
                                lastName: client.lastName,
                                email: client.email,
                                ci: client.ci,
                                observations: client.observations || undefined,
                                commercial:
                                  'commercial' in client && client.commercial
                                    ? {
                                        id: client.commercial.id || 0,
                                        name:
                                          'displayName' in client.commercial
                                            ? client.commercial.displayName
                                            : 'name' in client.commercial
                                              ? (client.commercial
                                                  .name as string)
                                              : '',
                                      }
                                    : undefined,
                                phones: client.phones?.map((phone) => ({
                                  number: phone.number,
                                  codeCountry: phone.codeCountry || '',
                                  isMain: phone.isMain || false,
                                })),
                              }
                              handleClientSelect(clientData)
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p
                                  className={cn(
                                    'font-medium',
                                    isSelected && 'text-primary'
                                  )}
                                >
                                  {client.name ||
                                    `${client.firstName || ''} ${client.lastName || ''}`.trim()}
                                  {isSelected && (
                                    <span className="ml-2 text-xs font-normal text-primary">
                                      (Seleccionado)
                                    </span>
                                  )}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {client.email || 'Sin email'}
                                </p>
                                {client.ci && (
                                  <p className="text-xs text-muted-foreground">
                                    CI: {client.ci}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      }
                    )}

                    <div className="p-2 text-center text-sm text-muted-foreground">
                      {isLoadingMore && 'Cargando más clientes...'}
                    </div>
                  </>
                ) : (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    {searchQuery.trim()
                      ? `No se encontraron clientes que coincidan con "${searchQuery}"`
                      : 'No se encontraron clientes.'}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        {!justSelector && (
          <Button
            className="h-16 gap-1"
            disabled={disabled}
            onClick={handleNewClientClick}
            size="lg"
            variant="outline"
          >
            <PlusCircle className="h-4 w-4" />
            Nuevo Cliente
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  )
}
